import { useCallback, useEffect, useRef } from 'react';
import { GameLobby } from '@/components/GameLobby';
import { HowToPlay } from '@/components/HowToPlay';
import { GameHUD } from '@/components/GameHUD';
import { GameOver } from '@/components/GameOver';
import { GameCanvas } from '@/game/GameCanvas';
import { useGameState } from '@/hooks/useGameState';
import { useGameAudio } from '@/hooks/useGameAudio';
import {
  trackPlayByType,
  trackMatch,
  emitGameEvent,
} from '@/looplay';
export function HomePage() {
  const {
    gameState,
    currentScore,
    bestScore,
    scoreRef,
    startedAtMsRef,
    startGame,
    incrementScore,
    endGame,
    resetGame,
    setHowToPlay,
    returnToLobby,
  } = useGameState();
  const { playHit } = useGameAudio();
  const hasPlayedHitRef = useRef(false);
  const handleStartGame = useCallback(() => {
    hasPlayedHitRef.current = false;
    startGame();
  }, [startGame]);
  const handleHowToPlay = useCallback(() => {
    setHowToPlay(true);
  }, [setHowToPlay]);
  const handleCloseHowToPlay = useCallback(() => {
    setHowToPlay(false);
  }, [setHowToPlay]);
  const handleScoreUpdate = useCallback(
    (delta: number) => {
      incrementScore(delta);
    },
    [incrementScore]
  );
  const handleGameOver = useCallback(() => {
    if (!hasPlayedHitRef.current) {
      hasPlayedHitRef.current = true;
    }
    const finalScore = Math.floor(scoreRef.current);
    const startedAt = startedAtMsRef.current;
    endGame();
    if (startedAt) {
      const playTimeSeconds = (Date.now() - startedAt) / 1000;
      void trackPlayByType({
        trackingType: 'survival',
        startedAtMs: startedAt,
      });
      void trackMatch(`match_${startedAt}`, {
        durationSeconds: Math.round(playTimeSeconds),
        isCompleted: true,
        isWin: false,
      });
    }
    if (finalScore > 0) {
      void emitGameEvent('high_score', { value: finalScore });
    }
  }, [endGame, scoreRef, startedAtMsRef]);
  const handleRestart = useCallback(() => {
    hasPlayedHitRef.current = false;
    resetGame();
    setTimeout(() => {
      startGame();
    }, 50);
  }, [resetGame, startGame]);
  const handleBackToMenu = useCallback(() => {
    hasPlayedHitRef.current = false;
    resetGame();
  }, [resetGame]);
  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
    };
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, []);
  return (
    <div
      className="h-[100dvh] w-full overflow-hidden relative"
      style={{
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {gameState === 'lobby' && (
        <GameLobby onStartGame={handleStartGame} onHowToPlay={handleHowToPlay} />
      )}
      {gameState === 'howToPlay' && (
        <GameLobby onStartGame={handleStartGame} onHowToPlay={handleHowToPlay} />
      )}
      {(gameState === 'playing' || gameState === 'gameOver') && (
        <>
          <GameHUD currentScore={currentScore} bestScore={bestScore} />
          <GameCanvas
            onScoreUpdate={handleScoreUpdate}
            onGameOver={handleGameOver}
            isPlaying={gameState === 'playing'}
          />
          {gameState === 'gameOver' && (
            <GameOver
              finalScore={currentScore}
              bestScore={bestScore}
              isNewBest={currentScore >= bestScore && currentScore > 0}
              onRestart={handleRestart}
              onMenu={handleBackToMenu}
            />
          )}
        </>
      )}
      {gameState === 'howToPlay' && (
        <HowToPlay onClose={handleCloseHowToPlay} onStartGame={handleStartGame} />
      )}
    </div>
  );
}